import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';

const LIST_ITEM_HEIGHT: number = 34;

@Component({
  selector: 'dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit {

  private _label: string;

  @Input() form: FormGroup;
  @Input() list: Array<string>;
  @Input() listPartTwo: Array<string>;
  @Input() listPartThree: Array<string>;
  @Input() listID: string;
  @Input()
  set label(value: string) {
    this._label = value;
    this.setPositions(23);
  }
  get label() {
    return this._label;
  }
  @Input() defaultItem: string;
  @Input() defaultText: string;
  @Input() listItemSubText: Array<number>;
  @Input() includeBlank: boolean = false;

  @Output() setFormControlName: EventEmitter<any> = new EventEmitter();

  firstInListPosition: number;
  secondInListPosition: number;
  thirdInListPosition: number;
  fourthInListPosition: number;
  fifthInListPosition: number;
  middleListPosition: number;
  thirdToLastInListPosition: number;
  secondToLastInListPosition: number;
  lastInListPosition: number;

  public selectedItem: string;
  public showList: boolean = false;
  public listTopPosition: number;
  public listHeight: number;

  public numberOfListItemsDisplayedInDropdown: number = 7;
  public dropdownCenterOffset: number = (LIST_ITEM_HEIGHT * this.numberOfListItemsDisplayedInDropdown) / 2 - ((LIST_ITEM_HEIGHT / 2) - 1);

  public centerItemIndex = 0;

  constructor() {
    this.setPositions(0);
  }

  // Runs whenever component @Inputs change
  ngOnChanges() {
    // Check if the data exists before using it
    if (this.list) {
      this.buildDropdown();

      if (this.includeBlank) {
        this.insertBlankOption();
      }

    }
  }

  ngOnInit() {
    if (this.includeBlank) {
      this.insertBlankOption();
    }

    this.buildDropdown();
  }

  private setPositions(value: number) {
    this.firstInListPosition = value;
    this.secondInListPosition= this.firstInListPosition - LIST_ITEM_HEIGHT;
    this.thirdInListPosition = this.secondInListPosition - LIST_ITEM_HEIGHT;
    this.fourthInListPosition = this.thirdInListPosition - LIST_ITEM_HEIGHT;
    this.fifthInListPosition = this.fourthInListPosition - LIST_ITEM_HEIGHT;
    this.middleListPosition = this.thirdInListPosition - LIST_ITEM_HEIGHT;
    this.thirdToLastInListPosition = this.middleListPosition - LIST_ITEM_HEIGHT;
    this.secondToLastInListPosition = this.thirdToLastInListPosition - LIST_ITEM_HEIGHT;
    this.lastInListPosition = this.secondToLastInListPosition - LIST_ITEM_HEIGHT;
  }

  public buildDropdown() {
    this.listHeight = this.getDropdownHeight(this.list);
    if (!this.defaultText || this.defaultItem) {
      this.setSelectedItem(this.defaultItem || this.list[0], false);
    }
  }

  public scrollToPreSelectedItem(event, listID, selectedItem, list) {
    if (event.type === 'keydown') {
      this.handleDropdownKeyboardEvent(event, listID, selectedItem, list);
    } else {
      this.openList(listID, selectedItem, list);
    }
  }

  public handleDropdownKeyboardEvent(event, listID, selectedItem, list) {
    var key = event.key || event.keyCode;

    switch (key) {
      case 'Enter':
      case 'NumpadEnter':
      case 13:
      case 'Space':
      case 'Spacebar':
      case ' ':
      case 32:
        event.preventDefault();
        if (this.showList) {
          this.setSelectedItem(this.selectedItem, true)
        } else {
          this.openList(listID, selectedItem, list);
        }
        break;
      case 'ArrowUp':
      case 'Up':
      case 38:
      case 'ArrowLeft':
      case 'Left':
      case 37:
        event.preventDefault();
        if (this.showList) {
          this.selectPrevious();
        } else {
          this.openList(listID, selectedItem, list);
        }
        break;
      case 'ArrowDown':
      case 'Down':
      case 40:
      case 'ArrowRight':
      case 'Right':
      case 39:
        event.preventDefault();
        if (this.showList) {
          this.selectNext();
        } else {
          this.openList(listID, selectedItem, list);
        }
        break;
      case 'Escape':
      case 'Esc':
      case 27:
      case 'Tab':
      case 9:
        if (this.showList) {
          this.setSelectedItem(this.selectedItem, true)
        }
        this.hideList();
    }
  }

  public setSelectedItem(item, isTogglingList=false) {
    this.defaultText = '';
    this.selectedItem = item;
    if (isTogglingList) {
      this.toggleList();
    }
    this.setFormControlName.emit(item);
    setTimeout(() => {
      this.listTopPosition = this.getDropdownPosition(this.selectedItem, this.list);
    }, 200);
  }

  public openList(listID, selectedItem, list) {
    this.positionSelection(listID, selectedItem, list);
    this.positionList();
  }

  public hideList() {
    if (this.showList) {
      this.showList = false;
    }
  }

  private positionList() {
    this.listTopPosition = this.getDropdownPosition(this.selectedItem, this.list);
    this.toggleList();
  }

  private toggleList() {
    this.showList = !this.showList;
  }

  private positionSelection(listID, listItem, list) {
    const listElement = document.getElementById(listID);
    const listItemElement = this.getListDomElement(listItem, list);
    const listElementTop = listElement.getBoundingClientRect().top;
    let listItemElementTop;
    if (listItemElement) {
      listItemElementTop = listItemElement.getBoundingClientRect().top;
    }
    this.centerItemIndex = list.indexOf(listItem);
    this.centerSelection(listElement, listItemElementTop, listElementTop);
  }

  private getListDomElement(listItem, list) {
    let item;
    listItem ? (item = listItem) : (item = list[0]);
    return document.getElementById(item);
  }

  private centerSelection(list, listItemTop, listTop) {
    list.scrollTop += (listItemTop - listTop) - this.dropdownCenterOffset;
  }

  private getDropdownHeight(list) {
    if (list.length > 6) {
      return LIST_ITEM_HEIGHT * this.numberOfListItemsDisplayedInDropdown;
    } else {
      return LIST_ITEM_HEIGHT * list.length;
    }
  }

  private getDropdownPosition(selectedItem, list) {
    if (selectedItem === list[0] || !selectedItem) {
      return this.firstInListPosition;
    } else if (selectedItem === list[1]) {
      return this.secondInListPosition;
    } else if (selectedItem === list[2]) {
      return this.thirdInListPosition;
    } else if (selectedItem === list[3]) {
      return this.fourthInListPosition;
    } else if (selectedItem === list[4]) {
      return this.fifthInListPosition;
    } else if (list.length > 6 && selectedItem === list[list.length - 3]) {
      return this.thirdToLastInListPosition;
    } else if (list.length > 6 && selectedItem === list[list.length - 2]) {
      return this.secondToLastInListPosition;
    } else if (list.length > 6 && selectedItem === list[list.length - 1]) {
      return this.lastInListPosition;
    } else {
      return this.middleListPosition;
    }
  }

  private selectNext() {
    const nextItemIndex = this.list.indexOf(this.selectedItem) + 1;
    if (nextItemIndex < this.list.length) {
      this.selectedItem = this.list[nextItemIndex];
      if (this.shouldScrollList(nextItemIndex)) {
        const newCenterItemIndex = this.list.indexOf(this.selectedItem) - 3
        this.positionSelection(this.listID, this.list[newCenterItemIndex], this.list)
      }
    }
  }

  private selectPrevious() {
    const previousItemIndex = this.list.indexOf(this.selectedItem) - 1;
    if (previousItemIndex >= 0) {
      this.selectedItem = this.list[previousItemIndex];
      if (this.shouldScrollList(previousItemIndex)) {
        const newCenterItemIndex = this.list.indexOf(this.selectedItem) + 3
        this.positionSelection(this.listID, this.list[newCenterItemIndex], this.list)
      }
    }
  }

  private shouldScrollList(nextItemIndex) {
    if (this.list.length > 7) {
      const isTopOfTheList = this.centerItemIndex < 3;
      const isBottomOfTheList = this.list.length - this.centerItemIndex <= 4;
      const isMiddleOfTheList = !isTopOfTheList && !isBottomOfTheList;

      if (isTopOfTheList && nextItemIndex > 6) {
        return true;
      } else if (isBottomOfTheList && (this.list.length - nextItemIndex > 3)) {
        return true;
      } else if (isMiddleOfTheList && Math.abs(this.centerItemIndex - nextItemIndex) > 3) {
        return true;
      }
    }
    return false;
  }

  private insertBlankOption() {
    if (this.list[0] !== '') {
      this.list.unshift('');
    }
  }
}
